import React, { useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { noop, result } from 'underscore';
import classNames from 'classnames';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { Text } from '@fluentui/react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { isFinalUrlAlignedWithStoreDomain, getTopLevelDomainFromUrl } from './shared';
import { getStyles } from './style';

export function withStoreUrl(WrappedComponent) {
  const HOC = (props) => {
    const {
      i18n,
      validationProps,
      updateValidation,
      storeDetails,
    } = props;

    const classes = useDefaultStyles(getStyles);
    const storeDetailsInternal = useRef(null);

    useMemo(() => {
      const storeUrl = storeDetails && storeDetails.storeUrl;
      if (storeUrl) {
        const storeDomain = getTopLevelDomainFromUrl(storeUrl);
        storeDetailsInternal.current = { url: storeUrl, domain: storeDomain };
      }
    }, [storeDetails]);

    const validationOnUrlChange = useCallback((value) => {
      const storeDomain = result(storeDetailsInternal.current, 'domain', null);
      let finalUrlValidatorError;
      if (storeDomain) {
        finalUrlValidatorError = isFinalUrlAlignedWithStoreDomain(value, storeDomain) ?
          null :
          i18n.getString(_TL_('Final URL domain does not match merchant center store URL'));
      }
      return finalUrlValidatorError;
    }, [i18n]);

    const storeUrl = result(storeDetailsInternal.current, 'url', null);
    const storeUrlText = useMemo(() => (storeUrl &&
    <div className="store-url-text-container">
      <Text>{ i18n.getString(_TL_('Merchant center store URL: {{storeUrl}}'), { storeUrl }) }</Text>
    </div>
    ), [i18n, storeUrl]);

    return (
      <React.Fragment>
        <div className={classNames('final-url-container', classes.UrlContainer)}>
          {storeUrlText}
          <WrappedComponent
            {...props}
            i18n={i18n}
            validationOnUrlChange={validationOnUrlChange}
            prepopulateUrl={storeUrl}
            updateValidation={updateValidation}
            validationProps={validationProps}
          />
        </div>
      </React.Fragment>
    );
  };

  HOC.propTypes = {
    i18n: PropTypes.shape({
      getString: PropTypes.func.isRequired,
    }).isRequired,
    providerId: PropTypes.number,
    updateValidation: PropTypes.func,
    storeDetails: PropTypes.shape({
      storeUrl: PropTypes.string,
    }),
    validationProps: PropTypes.shape({
      data: PropTypes.shape({
        FinalUrls: PropTypes.arrayOf(PropTypes.string),
      }),
      updateOnMount: PropTypes.bool,
    }).isRequired,
  };

  HOC.defaultProps = {
    providerId: undefined,
    updateValidation: noop,
    storeDetails: undefined,
  };

  HOC.displayName = `withStoreUrl(${getDisplayName(WrappedComponent)})`;
  hoistNonReactStatics(HOC, WrappedComponent);

  return HOC;
}
